// src/theme/theme.js
import { createTheme } from "@mui/material/styles";
import { BRAND_BLUE, BRAND_GREEN } from "./theme/colors";

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND_BLUE,
    },
    secondary: {
      main: BRAND_GREEN,
    },
  },

  components: {
    /* ===============================
       GLOBAL FORM CONTROL
    =============================== */
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: 160,
        },
      },
    },

    /* ===============================
       TEXT FIELDS
    =============================== */
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        color: "primary",
      },
    },

    /* ===============================
       INPUT BASE
    =============================== */
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: BRAND_BLUE,
          fontSize: "0.95rem",
          fontWeight: 700,
          letterSpacing: "0.2px",

          "&::placeholder": {
            fontWeight: 700,
            color: BRAND_BLUE,
            opacity: 0.55,
          },
        },

        multiline: {
          padding: "12px 14px",
        },

        inputMultiline: {
          lineHeight: 1.5,
        },
      },
    },

    /* ===============================
       OUTLINED INPUT
    =============================== */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "#ffffff",
          transition: "all 0.2s ease",

          // 🔥 Standardized compact height for ALL inputs
          "&:not(.MuiInputBase-multiline)": {
            height: 42,
          },

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 3,
          },
        },
      },
    },

    /* ===============================
       INPUT LABELS (FIXED SHRINK BEHAVIOR)
    =============================== */
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,
          fontWeight: 700,
          fontSize: "0.95rem",
          transform: "translate(14px, 12px) scale(1)",

          "&.Mui-focused": {
            color: BRAND_BLUE,
          },

          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -8px) scale(0.75)",
            color: BRAND_BLUE,
          },

          "&.Mui-error": {
            color: BRAND_BLUE,
          },

          "&.Mui-disabled": {
            color: BRAND_BLUE,
          },
        },
      },
    },

    /* ===============================
       SELECT (FIXED VERTICAL ALIGNMENT)
    =============================== */
    MuiSelect: {
      styleOverrides: {
        select: {
          color: BRAND_BLUE,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          height: "100%",
        },
        icon: {
          color: BRAND_BLUE,
        },
      },
    },

    /* ===============================
       AUTOCOMPLETE
    =============================== */
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
            height: 42,
          },
        },
        popupIndicator: {
          color: BRAND_BLUE,
        },
        clearIndicator: {
          color: BRAND_BLUE,
        },
      },
    },

    MuiPickersTextField: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#ffffff",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 3,
          },
        },
      },
    },

    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "#ffffff",
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 2.5,
          },
          "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: BRAND_BLUE,
            borderWidth: 3,
          },
        },
      },
    },

    /* ===============================
       BUTTONS
    =============================== */
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 10,
          fontWeight: 600,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: BRAND_BLUE,
          color: "#ffffff",
        },
        arrow: {
          color: BRAND_BLUE,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          overflow: "hidden",
          backgroundClip: "padding-box",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: "inherit",
          borderTopRightRadius: "inherit",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
        },
      },
    },

    /* ===============================
       ACCORDION
    =============================== */
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: `2px solid ${BRAND_BLUE}`,
          borderRadius: 14,
          boxShadow: "0 6px 18px rgba(0, 51, 108, 0.10)",
          overflow: "hidden",
          margin: 0,

          "&:before": {
            display: "none",
          },

          "&.Mui-expanded": {
            borderRadius: 14,
            margin: 0,
          },

          "& + &": {
            marginTop: 16,
          },

          "&:first-of-type": {
            borderRadius: 14,
          },

          "&:last-of-type": {
            borderRadius: 14,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,51,108,0.04)",
          minHeight: 56,
          borderRadius: 14,

          "&.Mui-expanded": {
            minHeight: 56,
            borderRadius: 14,
          },
        },
      },
    },

    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "20px 24px",
          backgroundColor: "#ffffff",
        },
      },
    },

    /* ===============================
       TABS
    =============================== */
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: BRAND_BLUE,
          height: 4,
          borderRadius: 2,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,
          fontWeight: 600,
          textTransform: "none",
          fontSize: 16,
          minHeight: 48,
          paddingLeft: 24,
          paddingRight: 24,

          "&.Mui-selected": {
            color: BRAND_BLUE,
            fontWeight: 700,
          },
        },
      },
    },

    /* ===============================
       CHECKBOX
    =============================== */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,

          "&.Mui-checked": {
            color: BRAND_BLUE,
          },

          "&.MuiCheckbox-indeterminate": {
            color: BRAND_BLUE,
          },
        },
      },
    },

    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1.5px solid ${BRAND_BLUE}`,
          boxShadow: "0 6px 24px rgba(0,51,108,0.16)",
          overflow: "hidden",
        },
      },
    },

    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: BRAND_BLUE,
          fontWeight: 700,
          fontSize: "1rem",
        },
        switchViewButton: {
          color: BRAND_BLUE,
        },
      },
    },

    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: BRAND_BLUE,
        },
      },
    },

    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          color: BRAND_BLUE,
          fontWeight: 600,
          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
            fontWeight: 700,
            "&:hover": { backgroundColor: BRAND_BLUE },
          },
          "&:hover": { backgroundColor: "rgba(0,51,108,0.08)" },
        },
      },
    },

    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          color: BRAND_BLUE,
          fontWeight: 600,
          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
            fontWeight: 700,
            "&:hover": { backgroundColor: BRAND_BLUE },
          },
          "&:hover": { backgroundColor: "rgba(0,51,108,0.08)" },
        },
      },
    },

    MuiClock: {
      styleOverrides: {
        pin: { backgroundColor: BRAND_BLUE },
        amButton: { color: BRAND_BLUE },
        pmButton: { color: BRAND_BLUE },
      },
    },

    MuiClockPointer: {
      styleOverrides: {
        root: { backgroundColor: BRAND_BLUE },
        thumb: {
          backgroundColor: BRAND_BLUE,
          borderColor: BRAND_BLUE,
        },
      },
    },

    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,
          fontWeight: 600,
          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
          },
        },
      },
    },

    MuiTimeClock: {
      styleOverrides: {
        root: {
          "& .MuiClock-clock": {
            backgroundColor: "rgba(0,51,108,0.04)",
          },
        },
      },
    },

    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,

          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
            fontWeight: 700,
          },

          "&.Mui-selected:hover": {
            backgroundColor: BRAND_BLUE,
          },

          "&.MuiPickersDay-today": {
            borderColor: BRAND_BLUE,
          },
        },
      },
    },

    MuiDigitalClockItem: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,

          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
            fontWeight: 700,
          },

          "&.Mui-selected:hover": {
            backgroundColor: BRAND_BLUE,
          },
        },
      },
    },

    MuiMultiSectionDigitalClockSectionItem: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,

          "&.Mui-selected": {
            backgroundColor: BRAND_BLUE,
            color: "#ffffff",
            fontWeight: 700,
          },

          "&.Mui-selected:hover": {
            backgroundColor: BRAND_BLUE,
          },
        },
      },
    },

    /* ===============================
       TABLE SORT LABEL
    =============================== */
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: BRAND_BLUE,

          "&.Mui-active": {
            color: BRAND_BLUE,
          },

          "& .MuiTableSortLabel-icon": {
            color: BRAND_BLUE,
          },
        },
      },
    },
  },
});

export default theme;
